import { GenericDatepicker } from '../adapter/DatepickerAdapter.js';
import { genericDatepickerInstances } from './GenericDatepickerRegistry.js';

/**
 * Fluent builder for GenericDatepicker configuration.
 */
export class GenericDatepickerBuilder {
  static instances = genericDatepickerInstances;
  constructor(selector) {
    this._selector = selector;
    this._valueExplicit = false;
    this._config = {
      value: window.GenericDateTime?.now().toDate?.() ?? new Date(),
      min: null,
      max: null,
      range: false,
      rangeSeparator: ' – ',
      components: {
        year: true,
        month: true,
        date: true,
        hours: true,
        minutes: true,
        seconds: false,
      },
      display: {
        year: true,
        month: true,
        date: true,
        hours: true,
        minutes: true,
        seconds: false,
      },
      timeToggle: false,
      filters: {
        date: null,
        month: null,
        year: null,
        hour: null,
        minute: null,
        second: null,
      },
      compactTime: false,
      onSelect: null,
      onOpen: null,
      onClose: null,
      onChange: null,
    };
  }

  static create(selector) {
    return new GenericDatepickerBuilder(selector);
  }

  withValue(value) {
    this._valueExplicit = true;
    if (value instanceof Date) {
      this._config.value = value;
    } else if (
      Array.isArray(value) ||
      (value && typeof value === 'object' && ('start' in value || 'end' in value))
    ) {
      this._config.value = value;
    } else if (value === null) {
      this._config.value = null;
    } else if (value == null) {
      this._config.value = null;
    } else {
      this._config.value = new Date(value);
    }
    return this;
  }

  withNullDefault() {
    return this.withValue(null);
  }

  withRange(enabled = true) {
    this._config.range = enabled;
    if (enabled && !this._valueExplicit) {
      this._config.value = null;
    }
    return this;
  }

  withRangeSeparator(separator) {
    this._config.rangeSeparator = separator ?? ' – ';
    return this;
  }
  withMinDate(date) {
    this._config.min = date instanceof Date ? date : new Date(date);
    return this;
  }
  withMaxDate(date) {
    this._config.max = date instanceof Date ? date : new Date(date);
    return this;
  }
  withLocale(locale) {
    this._config.locale = locale;
    return this;
  }

  withYear(enabled = true) {
    this._config.components.year = enabled;
    return this;
  }
  withMonth(enabled = true) {
    this._config.components.month = enabled;
    return this;
  }
  withDate(enabled = true) {
    this._config.components.date = enabled;
    return this;
  }
  withHours(enabled = true) {
    this._config.components.hours = enabled;
    return this;
  }
  withMinutes(enabled = true) {
    this._config.components.minutes = enabled;
    return this;
  }
  withSeconds(enabled = true) {
    this._config.components.seconds = enabled;
    return this;
  }

  withTimeOnly() {
    this._config.components.year = false;
    this._config.components.month = false;
    this._config.components.date = false;
    this._config.components.hours = true;
    this._config.components.minutes = true;

    this._config.display.year = false;
    this._config.display.month = false;
    this._config.display.date = false;
    this._config.display.hours = true;
    this._config.display.minutes = true;
    return this;
  }

  withDateOnly() {
    this._config.components.year = true;
    this._config.components.month = true;
    this._config.components.date = true;
    this._config.components.hours = false;
    this._config.components.minutes = false;
    this._config.components.seconds = false;

    this._config.display.year = true;
    this._config.display.month = true;
    this._config.display.date = true;
    this._config.display.hours = false;
    this._config.display.minutes = false;
    this._config.display.seconds = false;
    return this;
  }

  withTimeToggle(enabled = true) {
    this._config.timeToggle = enabled;
    return this;
  }

  withCompactTime(enabled = true) {
    this._config.compactTime = enabled;
    return this;
  }

  /**
   * Explicit output template for the input display, using GenericDateTime token
   * grammar (YYYY MM DD HH hh mm ss A a + literals). Once set, this template is
   * authoritative for both the value display and the empty-state mask.
   * When unset, the default display format (12-hour + AM/PM) applies.
   * @param {string} template e.g. 'MM/DD/YYYY HH:mm' for 24-hour clock.
   */
  withFormat(template) {
    if (!template) {
      this._config.displayFormat = null;
      return this;
    }
    this._config.displayFormat = template;
    return this;
  }

  withDateFilter(fn) {
    this._config.filters.date = fn;
    return this;
  }
  withMonthFilter(fn) {
    this._config.filters.month = fn;
    return this;
  }
  withYearFilter(fn) {
    this._config.filters.year = fn;
    return this;
  }
  withHourFilter(fn) {
    this._config.filters.hour = fn;
    return this;
  }
  withMinuteFilter(fn) {
    this._config.filters.minute = fn;
    return this;
  }
  withSecondFilter(fn) {
    this._config.filters.second = fn;
    return this;
  }

  withYearValue(enabled = true) {
    this._config.display.year = enabled;
    return this;
  }
  withMonthValue(enabled = true) {
    this._config.display.month = enabled;
    return this;
  }
  withDateValue(enabled = true) {
    this._config.display.date = enabled;
    return this;
  }
  withHoursValue(enabled = true) {
    this._config.display.hours = enabled;
    return this;
  }
  withMinutesValue(enabled = true) {
    this._config.display.minutes = enabled;
    return this;
  }
  withSecondsValue(enabled = true) {
    this._config.display.seconds = enabled;
    return this;
  }

  withTheme(theme) {
    this._config.theme = theme;
    return this;
  }

  onSelect(callback) {
    this._config.onSelect = callback;
    return this;
  }
  onOpen(callback) {
    this._config.onOpen = callback;
    return this;
  }
  onClose(callback) {
    this._config.onClose = callback;
    return this;
  }
  onChange(callback) {
    this._config.onChange = callback;
    return this;
  }

  build() {
    const $ = window.jQuery;
    const el = resolveBuildTarget(this._selector);
    const instance = new GenericDatepicker(this._selector, this._config);

    instance._registryKey = GenericDatepickerBuilder.instances.register(el ?? this._selector, instance);

    if ($ && el) {
      $(el).data('generic-datepicker', instance);
    }

    return instance;
  }
}

function resolveBuildTarget(selector) {
  const $ = window.jQuery;
  if (!selector) return null;
  if (selector instanceof HTMLElement) return selector;
  if (selector?.nodeType === 1) return selector;
  if ($ && selector?.jquery) return selector[0] ?? null;
  if ($ && typeof selector === 'string') return $(selector)[0] ?? null;
  return null;
}
